import { FormattedMessage } from "react-intl";
import { Navbar, Nav, NavDropdown, MenuItem, NavItem } from "react-bootstrap";
import { v4 as uuid } from "uuid";

import { intlShape, sessionShape, locationShape } from "../../shapes";
import CONFIGURATION from "../../configuration";
import SeatInvitationCheck from "../Seats/SeatInvitationCheck";

import {
  withConfiguration,
  withSession,
  withBrowser,
  browserShape,
} from "../../context";
import NavigationAppSwitcher from "./NavigationAppSwitcher";
import NavigationUserMenu from "./NavigationUserMenu";
import { NavigationItemExternalLink } from "./NavigationItemExternalLink";
import { withIntl } from "../../wrappers";
import HeaderSearch from "../HeaderSearch/HeaderSearch";
import { sentryUnmask } from "../../utils/sentryUtils";

const mapNavigationItem = (
  item: $TSFixMe,
  location: $TSFixMe,
  intl: $TSFixMe,
  browser: $TSFixMe,
) => {
  const {
    active,
    translationKey,
    target,
    className,
    isExternalLink,
    externalLinkTargetName,
    onClick,
  } = item.props;
  const isActive = location && active?.test(location.pathname);
  const label = translationKey
    ? intl.formatMessage({ id: translationKey })
    : "";
  if (isExternalLink) {
    return (
      <NavigationItemExternalLink
        key={uuid()}
        href={target}
        target={externalLinkTargetName}
        label={label}
        className={sentryUnmask(className)}
      />
    );
  }
  return (
    <NavItem
      className={sentryUnmask(className)}
      active={isActive}
      onClick={() => {
        if (onClick) {
          onClick();
        } else {
          browser.navigateTo(target);
        }
      }}
      key={uuid()}
    >
      {label}
    </NavItem>
  );
};

const mapMenuItem = (menuItem: $TSFixMe, browser: $TSFixMe) => {
  const { active, target, children } = menuItem.props;
  const label = children.props.id;
  const onClick = () => browser.navigateTo(target);
  return (
    <MenuItem onClick={onClick} active={active} key={uuid()}>
      {/* @ts-expect-error TS(2322) FIXME: Type '{ className: string; id: any; }' is not assi... Remove this comment to see the full error message */}
      <FormattedMessage className="sentry-unblock" id={label} />
    </MenuItem>
  );
};

const mapNavigationDropdownItem = (
  item: $TSFixMe,
  location: $TSFixMe,
  intl: $TSFixMe,
  browser: $TSFixMe,
) => {
  const { children, translationKey, className } = item.props;
  const isActive = location && item.props.active.test(location.pathname);
  const label = intl.formatMessage({ id: translationKey });
  if (children) {
    const validChildren = Array.isArray(children)
      ? children.filter((child: $TSFixMe) => typeof child === "object")
      : children;

    const menuItems = Array.isArray(validChildren)
      ? validChildren.map((child: $TSFixMe) => mapMenuItem(child, browser))
      : mapMenuItem(validChildren, browser);
    return (
      <NavDropdown
        id="subscription-navigation-dropdown"
        title={label}
        active={isActive}
        className={sentryUnmask(className)}
        key={uuid()}
      >
        {menuItems}
      </NavDropdown>
    );
  }
  return null;
};

const renderChild = (
  child: $TSFixMe,
  location: $TSFixMe,
  intl: $TSFixMe,
  browser: $TSFixMe,
) => {
  if (child.type?.componentName === "NavigationItem") {
    return mapNavigationItem(child, location, intl, browser);
  }
  if (child.type?.componentName === "NavigationDropdownItem") {
    return mapNavigationDropdownItem(child, location, intl, browser);
  }
  if (child.key === "NavigationItemClickable") {
    return child;
  }

  return null;
};

const renderNavigationItems = (
  children: $TSFixMe,
  location: $TSFixMe,
  intl: $TSFixMe,
  browser: $TSFixMe,
) => {
  if (!children) {
    return null;
  }
  if (children.map) {
    return children
      .filter((child: $TSFixMe) => child && child.type)
      .map((child: $TSFixMe) => renderChild(child, location, intl, browser));
  }
  return renderChild(children, location, intl, browser);
};

type OwnNavigationBarProps = {
  name?: string;
  mobileName?: string;
  session?: sessionShape;
  location?: locationShape;
  children?: React.ReactNode;
  intl: intlShape;
  showAppSwitcher?: boolean;
  showSearchBar?: boolean;
  showNavigationItems?: boolean;
  showSupportItem?: boolean;
  showDemoData?: boolean;
  browser?: browserShape;
  configuration: {
    environment?: string;
  };
};

const defaultProps: Partial<OwnNavigationBarProps> = {
  showAppSwitcher: true,
  showSearchBar: true,
};

type NavigationBarProps = OwnNavigationBarProps & typeof defaultProps;

export function NavigationBar({
  intl,
  name,
  browser,
  session,
  location,
  children,
  mobileName,
  showDemoData,
  configuration,
  showAppSwitcher,
  showSearchBar,
  showSupportItem,
  showNavigationItems,
}: NavigationBarProps) {
  const navigationItems =
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    session.authenticated && showNavigationItems
      ? renderNavigationItems(children, location, intl, browser)
      : null;
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const stripeShadowClassName = session.authenticated
    ? "cyan-stripe navigation-shadow"
    : "cyan-stripe";

  return (
    <div className="navigation">
      <div className={stripeShadowClassName}>
        <div className="container">
          <Navbar collapseOnSelect>
            <div className="row">
              <div className="navigation-header">
                <div className="navigation-toggle">
                  {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                  <Navbar.Toggle disabled={!session.authenticated} />
                </div>
                <div className="navigation-title">
                  <a href={`${CONFIGURATION.BASE_URL}`}>
                    <span className="hidden-xs hidden-sm">{name}</span>
                    {configuration.environment === "staging" && (
                      <span className="hidden-xs hidden-sm staging-hint">
                        {" "}
                        Staging
                      </span>
                    )}
                    <span className="hidden-md hidden-lg">{mobileName}</span>
                    {configuration.environment === "staging" && (
                      <span className="hidden-md hidden-lg staging-hint">
                        {" "}
                        Staging
                      </span>
                    )}
                  </a>
                </div>
                {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                {session.authenticated && showSearchBar ? (
                  <div
                    className="mobile-search-overlay visible-xs"
                    id="mobile-search"
                  >
                    {/* @ts-expect-error TS(2322) FIXME: Type '{ placeholder: string; }' is not assignable ... Remove this comment to see the full error message */}
                    <HeaderSearch placeholder="Search in Netilion" />
                  </div>
                ) : null}
                <div className="navigation-logo" />
              </div>
            </div>
            <Navbar.Collapse>
              <div className="row">
                <Nav id="app-navigation">{navigationItems}</Nav>
                <Nav id="user-navigation" pullRight>
                  {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                  {session.authenticated && (
                    <NavigationUserMenu
                      showDemoData={showDemoData}
                      showSupportItem={showSupportItem}
                    >
                      {children &&
                        (children as $TSFixMe).map &&
                        (children as $TSFixMe).filter(
                          (child: $TSFixMe) =>
                            child &&
                            child.key !== "NavigationItemClickable" &&
                            child.type.componentName !== "NavigationItem" &&
                            child.type.componentName !==
                              "NavigationDropdownItem",
                        )}
                    </NavigationUserMenu>
                  )}
                </Nav>
                {showAppSwitcher ? (
                  <Nav id="apps-navigation" pullRight>
                    {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                    {session.authenticated && (
                      // @ts-expect-error TS(2322) FIXME: Type '{ location: locationShape | undefined; }' is... Remove this comment to see the full error message
                      <NavigationAppSwitcher location={location} />
                    )}
                  </Nav>
                ) : null}
                {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                {session.authenticated && showSearchBar ? (
                  <Nav
                    id="header-search"
                    className="hidden-xs"
                    pullRight
                    style={{ height: "auto" }}
                  >
                    {/* @ts-expect-error TS(2322) FIXME: Type '{ placeholder: string; }' is not assignable ... Remove this comment to see the full error message */}
                    <HeaderSearch placeholder="Search in Netilion" />
                  </Nav>
                ) : null}
              </div>
            </Navbar.Collapse>
          </Navbar>
          <SeatInvitationCheck />
        </div>
      </div>
      <div className="navigation-separator" />
    </div>
  );
}

NavigationBar.defaultProps = defaultProps;

export default withConfiguration(
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ForwardRefExoticComponent<Omit<O... Remove this comment to see the full error message
  withBrowser(withSession(withIntl(NavigationBar))),
);
